import { x } from '@xstyled/styled-components';
import React, { DragEventHandler, useEffect } from 'react';
import audioEffectRightAnswer from '~/assets/mp3/right_answer.mp3';
import audioEffectWrongAnswer from '~/assets/mp3/wrong_answer.mp3';
import { select } from '~/lib/select';
import { useSequenceStore } from '~/training/sequence/SequenceStore';
import { DROP_ZONE } from '~/training/sequence/templates/items';
import { useDragAndDropStore } from '~/training/sequence/templates/items/drag-and-drop/DragAndDropStore';
import { useAudioEffect } from '~/ui/audio-effect/useAudioEffect';
import { Capsule } from '~/ui/capsules';
import { Card } from '~/ui/card/Card';
import { Flex } from '~/ui/flex/Flex';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { Item, ItemDefinition, registry, RenderProps } from '../Item';
import { CorrectBadge } from '../quiz-match/components/CorrectBadge';
import { IncorrectBadge } from '../quiz-match/components/IncorrectBadge';
import styled from '@xstyled/styled-components';
import TrashIcon from '~/assets/icons/trash.svg';
import { t } from 'ttag';

const selector = (s: MediaPlayerStore) => select(s, 'loadMedia', 'reset');

const Trash = styled(TrashIcon)`
  float: right;
  margin-top: 5px;
  margin-right: -9px;
`;

interface Props {
  helpMessage?: string;
  items?: Array<{
    correct: boolean;
    feedback: string;
    feedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
    id: string;
    value: string;
  }>;
}

const DragAndDropItem = (props: {
  id: string;
  selected?: boolean;
  label?: string;
  correct: boolean;
  value: string;
  feedbackAudios?: Array<{ mediaUrl: string; subtitlesUrl?: string }>;
}) => {
  const { dropped, setDropped } = useDragAndDropStore();
  const wrong = props.selected && !props.correct;

  const onDragStart: DragEventHandler<HTMLDivElement> = event => {
    event.dataTransfer.setData('text/plain', props.id);
    event.dataTransfer.dropEffect = 'move';
    event.currentTarget.style.opacity = '0.9';
  };

  const droppedEvent = () => {
    setDropped(dropped.filter((i: string) => i !== props.id));
  };

  const onDragEnd: DragEventHandler<HTMLDivElement> = event => {
    const elemBelow = document.elementFromPoint(event.clientX, event.clientY);

    if (!elemBelow) return;

    if (elemBelow?.getAttribute('draggable')) {
      droppedEvent();
    }
  };

  const onDrop: DragEventHandler<HTMLDivElement> = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      droppedEvent();
    }
  };

  return (
    <Card
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDoubleClick={droppedEvent}
      onClick={droppedEvent}
      onDrop={onDrop}
      onKeyDown={handleKeyDown}
      p="sm lg"
      borderStyle="solid"
      borderWidth="1px"
      borderColor={wrong ? 'redDark' : 'greenDark'}
      cursor="pointer"
      margin="5px"
      tabIndex={0}
      aria-label={`${wrong ? t`Wrong` : t`Correct`} ${props?.label}`}
      aria-live="polite"
      role={'button'}
    >
      {wrong && (
        <>
          <IncorrectBadge
            aria-describedby={`${props.id}-desc`}
            aria-hidden={true}
          />
        </>
      )}
      {!wrong && (
        <>
          <CorrectBadge
            aria-describedby={`${props.id}-desc`}
            aria-hidden={true}
          />
        </>
      )}
      <Capsule
        aria-hidden={true}
        fontWeight="bold"
        float="left"
        marginRight={'10px'}
      >
        {props?.label}
      </Capsule>
      {wrong && (
        <Trash
          aria-hidden="true"
          style={{
            float: 'right',
            marginRight: '-12px',
            marginLeft: '-5px',
            marginTop: '-5px'
          }}
          onClick={() => droppedEvent()}
        />
      )}
      {props.value}
    </Card>
  );
};

export type DropZoneDefinition = ItemDefinition<typeof DROP_ZONE, Props>;

export class DropZone extends Item<typeof DROP_ZONE, Props> {
  protected render({ items, id, helpMessage }: RenderProps<Props>) {
    const { loadMedia, reset } = useMediaPlayerStore(selector);
    const { addBlocker, removeBlocker } = useSequenceStore();
    const { dropped, setDropped, lastId, setLastId } = useDragAndDropStore();
    const [playAudioEffectWrongAnswer] = useAudioEffect(audioEffectWrongAnswer);
    const [playAudioEffectRightAnswer] = useAudioEffect(audioEffectRightAnswer);

    useEffect(() => {
      if (lastId) {
        const item = items?.find(i => i.id == lastId);

        if (item && item.correct) {
          playAudioEffectRightAnswer();
        }

        if (item && !item.correct) {
          playAudioEffectWrongAnswer();

          if (item.feedbackAudios && item.feedbackAudios.length) {
            reset();
            loadMedia(
              item?.feedbackAudios[0]?.mediaUrl as string,
              item?.feedbackAudios[0]?.subtitlesUrl || '',
              'audio',
              true
            );
          }
        }
      }
    }, [lastId]);

    const correct =
      dropped
        .map(id => items?.find(i => i.id === id))
        .reduce((a, b) => b?.correct && a, true) &&
      dropped.length === items?.filter(i => i.correct).length;

    useEffect(() => {
      if (!correct) {
        addBlocker(id);
        return () => removeBlocker(id);
      }
    }, [correct]);

    items = items?.map((item, index) => {
      return { ...item, label: String.fromCharCode(97 + index) };
    });

    const onDragOver: DragEventHandler<HTMLDivElement> = e => {
      e.stopPropagation();
      e.preventDefault();
    };

    const onDrop: DragEventHandler<HTMLDivElement> = e => {
      const id = e.dataTransfer.getData('text/plain');
      const set = new Set([...dropped, id]);
      setDropped([...set]);
      setLastId(id);

      e.preventDefault();
      e.stopPropagation();
    };

    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        w="100%"
        h="100%"
      >
        <Flex
          id="drop_zone_box"
          // w="70%"
          minHeight="40px"
          w={{ _: '100%', md: '70%' }}
          flexDirection={'column'}
          border="2px dashed grey"
          onDragOver={onDragOver}
          onDrop={onDrop}
          aria-label={t`List of correct and incorrect items`}
          role={'listbox'}
        >
          {items
            ?.filter(i => dropped.includes(i.id))
            .map(i => (
              <DragAndDropItem selected key={i.id} {...i} />
            ))}
        </Flex>
        <Flex w={{ _: '80%', lg: '50%' }}>
          {!correct &&
            dropped.length > 0 &&
            (items?.filter(i => i.correct).length || 0 > 1) && (
              <Card
                role={'tooltip'}
                aria-live="polite"
                p="md"
                m="xxl"
                w={'auto'}
                dangerouslySetInnerHTML={{
                  __html:
                    helpMessage ||
                    t`In order to continue, please select all of the correct answers and remove any incorrect answers.`
                }}
              />
            )}
        </Flex>
      </Flex>
    );
  }
}

registry[DROP_ZONE] = DropZone;
